import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, CircularProgress, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, useTheme, Autocomplete, TextField, IconButton, Stack,
  ToggleButtonGroup, ToggleButton, Collapse, Divider, Avatar
} from '@mui/material';
import {
  Refresh as RefreshIcon, FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon, Print as PrintIcon,
  ExpandMore, ExpandLess, AccountTree, AttachMoney,
  Storefront, Restaurant, CalendarToday, DragIndicator,
  Close as CloseIcon, Add as AddIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import { branchesService } from '../../services/branches.service';
import axiosInstance from '../../services/axiosInstance';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ─────────── Types ────────────────────────────────────────────────
interface Aggregate {
  recordsCount: number;
  gross: number; discount: number; serviceCharge: number; net: number;
  delivery: number; dineIn: number; takeAway: number; cancelledOrders: number;
  byPaymentMethod: Record<string, number>;
  totalPayments: number;
}
interface GroupNode {
  groupBy: string;
  groupKey: string;
  groupValue: string;
  groupValueAr: string;
  level: number;
  aggregate: Aggregate;
  children: GroupNode[];
}
interface ReportResult {
  generatedAt: string;
  fromDate: string | null; toDate: string | null;
  branchIds: number[]; conceptIds: number[]; groupBy: string[];
  tree: GroupNode[];
  grandTotal: Aggregate;
  paymentMethodColumns: string[];
}

const GROUP_OPTIONS = [
  { value: 'branch', labelEn: 'Branch', labelAr: 'الفرع', icon: <Storefront /> },
  { value: 'concept', labelEn: 'Concept', labelAr: 'الكونسبت', icon: <Restaurant /> },
  { value: 'day', labelEn: 'Day', labelAr: 'اليوم', icon: <CalendarToday /> },
  { value: 'month', labelEn: 'Month', labelAr: 'الشهر', icon: <CalendarToday /> },
  { value: 'status', labelEn: 'Status', labelAr: 'الحالة', icon: <AccountTree /> },
];

const DailySalesSummaryReport: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { minDate, maxDate } = useFiscalDateRange();

  const [result, setResult] = useState<ReportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [branches, setBranches] = useState<any[]>([]);
  const [concepts, setConcepts] = useState<any[]>([]);

  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [selectedBranches, setSelectedBranches] = useState<number[]>([]);
  const [selectedConcepts, setSelectedConcepts] = useState<number[]>([]);
  const [groupBy, setGroupBy] = useState<string[]>(['branch']);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    companyInfoService.getCompanyInfo().then(setCompanyInfo).catch(() => {});
    branchesService.getAll().then(setBranches).catch(() => {});
    axiosInstance.get('/Concepts').then(r => setConcepts(r.data)).catch(() => {});
  }, []);

  const reportTitle = isRTL ? 'تقرير ملخص المبيعات اليومية (مرن)' : 'Daily Sales Summary (Flexible)';
  const compName = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;

  const fmt = (n: number | null | undefined) => (n == null ? '-' : Number(n).toFixed(3));

  const loadReport = async () => {
    if (groupBy.length === 0) {
      toast.warning(isRTL ? 'اختر طريقة تجميع واحدة على الأقل' : 'Select at least one group-by');
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('fromDate', `${fromDate.getFullYear()}-${String(fromDate.getMonth() + 1).padStart(2, '0')}-${String(fromDate.getDate()).padStart(2, '0')}`);
      if (toDate) params.append('toDate', `${toDate.getFullYear()}-${String(toDate.getMonth() + 1).padStart(2, '0')}-${String(toDate.getDate()).padStart(2, '0')}`);
      if (selectedBranches.length) params.append('branchIds', selectedBranches.join(','));
      if (selectedConcepts.length) params.append('conceptIds', selectedConcepts.join(','));
      params.append('groupBy', groupBy.join(','));
      const { data } = await axiosInstance.get(`/DailySalesSummaryReport?${params.toString()}`);
      setResult(data);
      // expand top-level by default
      const initialExpand = new Set<string>();
      (data.tree as GroupNode[]).forEach((n, i) => initialExpand.add(`L0_${i}`));
      setExpanded(initialExpand);
      toast.success(isRTL ? 'تم التحميل' : 'Loaded');
    } catch {
      toast.error(isRTL ? 'فشل التحميل' : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (key: string) => {
    setExpanded(prev => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };

  const expandAll = () => {
    if (!result) return;
    const all = new Set<string>();
    const walk = (nodes: GroupNode[], path: string) => {
      nodes.forEach((n, i) => {
        const k = `${path}_${i}`;
        all.add(k);
        if (n.children?.length) walk(n.children, k);
      });
    };
    walk(result.tree, 'L0');
    setExpanded(all);
  };
  const collapseAll = () => setExpanded(new Set());

  const addGroupBy = (val: string) => {
    if (!groupBy.includes(val)) setGroupBy([...groupBy, val]);
  };
  const removeGroupBy = (val: string) => setGroupBy(groupBy.filter(g => g !== val));
  const moveGroupBy = (idx: number, dir: -1 | 1) => {
    const next = [...groupBy];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= next.length) return;
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    setGroupBy(next);
  };

  const getGroupLabel = (val: string) => {
    const opt = GROUP_OPTIONS.find(o => o.value === val);
    return opt ? (isRTL ? opt.labelAr : opt.labelEn) : val;
  };

  // ─── Render group node recursively ─────────────────────────────────
  const renderNode = (node: GroupNode, path: string, depth: number = 0): React.ReactNode => {
    const key = path;
    const isExpanded = expanded.has(key);
    const hasChildren = node.children?.length > 0;
    const groupLabel = getGroupLabel(node.groupBy);
    const value = isRTL ? (node.groupValueAr || node.groupValue) : node.groupValue;

    const colorByDepth = ['#1565C0', '#2E7D32', '#FF8C00', '#6A1B9A', '#C62828'];
    const bgColor = colorByDepth[depth % colorByDepth.length];

    return (
      <React.Fragment key={key}>
        <TableRow sx={{
          bgcolor: isDark
            ? `rgba(${depth === 0 ? '21, 101, 192' : depth === 1 ? '46, 125, 50' : '255, 140, 0'}, ${0.15 - depth * 0.03})`
            : `rgba(${depth === 0 ? '21, 101, 192' : depth === 1 ? '46, 125, 50' : '255, 140, 0'}, ${0.08 - depth * 0.015})`,
          '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' }
        }}>
          <TableCell sx={{ pl: `${4 + depth * 14}px`, fontWeight: depth === 0 ? 700 : 600, fontSize: `${depth === 0 ? 11 : 10}px !important` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
              {hasChildren ? (
                <IconButton size="small" sx={{ p: '2px' }} onClick={() => toggleExpand(key)}>
                  {isExpanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
                </IconButton>
              ) : (
                <Box sx={{ width: 18 }} />
              )}
              <Chip size="small" label={groupLabel}
                sx={{ bgcolor: bgColor, color: 'white', fontSize: 8, height: 14, '& .MuiChip-label': { px: 0.5 } }} />
              <Typography sx={{ fontSize: depth === 0 ? 11 : 10, fontWeight: depth === 0 ? 700 : 600, overflow: 'hidden', textOverflow: 'ellipsis' }} title={value}>{value}</Typography>
              <Chip size="small" label={node.aggregate.recordsCount}
                sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.07)', fontSize: 9, height: 14, '& .MuiChip-label': { px: 0.5 } }} />
            </Box>
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: depth === 0 ? 700 : 500, fontSize: '10px !important' }}>{fmt(node.aggregate.gross)}</TableCell>
          <TableCell align="right" sx={{ color: '#E65100', fontSize: '10px !important' }}>{fmt(node.aggregate.discount)}</TableCell>
          <TableCell align="right" sx={{ fontSize: '10px !important' }}>{fmt(node.aggregate.serviceCharge)}</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, color: '#1565C0', fontSize: `${depth === 0 ? 11 : 10}px !important` }}>{fmt(node.aggregate.net)}</TableCell>
          <TableCell align="right" sx={{ fontSize: '9px !important' }}>{fmt(node.aggregate.dineIn)}</TableCell>
          <TableCell align="right" sx={{ fontSize: '9px !important' }}>{fmt(node.aggregate.takeAway)}</TableCell>
          <TableCell align="right" sx={{ fontSize: '9px !important' }}>{fmt(node.aggregate.delivery)}</TableCell>
          {result?.paymentMethodColumns.map(pm => (
            <TableCell key={pm} align="right" sx={{ fontSize: '9px !important' }}>
              {fmt(node.aggregate.byPaymentMethod[pm] || 0)}
            </TableCell>
          ))}
        </TableRow>
        {hasChildren && isExpanded && node.children.map((child, i) =>
          renderNode(child, `${key}_${i}`, depth + 1)
        )}
      </React.Fragment>
    );
  };

  // ─── Excel Export ──────────────────────────────────────────────────
  const exportToExcel = async () => {
    if (!result) return;
    try {
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet(isRTL ? 'الملخص' : 'Summary');

      const fixedCols = [
        isRTL ? 'المجموعة' : 'Group',
        isRTL ? 'إجمالي' : 'Gross',
        isRTL ? 'خصم' : 'Discount',
        isRTL ? 'خدمة' : 'Service',
        isRTL ? 'الصافي' : 'Net',
        isRTL ? 'صالة' : 'Dine-In',
        isRTL ? 'سفري' : 'Take-Away',
        isRTL ? 'دليفري' : 'Delivery',
      ];
      const headers = [...fixedCols, ...result.paymentMethodColumns];

      // Header band
      ws.mergeCells(1, 1, 1, headers.length);
      ws.getCell(1, 1).value = compName || '';
      ws.getCell(1, 1).font = { size: 18, bold: true, color: { argb: 'FF1565C0' } };
      ws.getCell(1, 1).alignment = { horizontal: 'center' };

      ws.mergeCells(2, 1, 2, headers.length);
      const tc = ws.getCell(2, 1);
      tc.value = reportTitle;
      tc.font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
      tc.alignment = { horizontal: 'center' };
      tc.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1565C0' } };

      ws.mergeCells(3, 1, 3, headers.length);
      ws.getCell(3, 1).value = `${isRTL ? 'من' : 'From'}: ${result.fromDate ? new Date(result.fromDate).toLocaleDateString('en-GB') : '-'}    ${isRTL ? 'إلى' : 'To'}: ${result.toDate ? new Date(result.toDate).toLocaleDateString('en-GB') : '-'}    ${isRTL ? 'مجمّع حسب' : 'Grouped by'}: ${result.groupBy.map(getGroupLabel).join(' → ')}`;
      ws.getCell(3, 1).alignment = { horizontal: 'center' };
      ws.getCell(3, 1).font = { italic: true, size: 11 };

      // Header row
      const hr = ws.getRow(5);
      headers.forEach((h, i) => {
        const c = hr.getCell(i + 1);
        c.value = h;
        c.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1565C0' } };
        c.alignment = { horizontal: 'center' };
      });

      // Body — flatten tree
      let r = 6;
      const groupColors = ['FFD0E2F4', 'FFD0EFD0', 'FFFFE0B2', 'FFE1BEE7', 'FFFFCDD2'];
      const flatten = (nodes: GroupNode[], depth: number) => {
        nodes.forEach(n => {
          const row = ws.getRow(r);
          const indent = '    '.repeat(depth);
          row.getCell(1).value = `${indent}[${getGroupLabel(n.groupBy)}] ${isRTL ? (n.groupValueAr || n.groupValue) : n.groupValue}  (${n.aggregate.recordsCount})`;
          row.getCell(2).value = Number(n.aggregate.gross);
          row.getCell(3).value = Number(n.aggregate.discount);
          row.getCell(4).value = Number(n.aggregate.serviceCharge);
          row.getCell(5).value = Number(n.aggregate.net);
          row.getCell(6).value = Number(n.aggregate.dineIn);
          row.getCell(7).value = Number(n.aggregate.takeAway);
          row.getCell(8).value = Number(n.aggregate.delivery);
          result.paymentMethodColumns.forEach((pm, i) => {
            row.getCell(9 + i).value = Number(n.aggregate.byPaymentMethod[pm] || 0);
          });
          if (depth < groupColors.length) {
            row.eachCell({ includeEmpty: true }, c => {
              c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: groupColors[depth] } };
            });
          }
          if (depth === 0) row.font = { bold: true };
          r++;
          if (n.children?.length) flatten(n.children, depth + 1);
        });
      };
      flatten(result.tree, 0);

      // Grand total
      const gt = ws.getRow(r);
      gt.getCell(1).value = isRTL ? 'الإجمالي العام' : 'GRAND TOTAL';
      gt.getCell(2).value = Number(result.grandTotal.gross);
      gt.getCell(3).value = Number(result.grandTotal.discount);
      gt.getCell(4).value = Number(result.grandTotal.serviceCharge);
      gt.getCell(5).value = Number(result.grandTotal.net);
      gt.getCell(6).value = Number(result.grandTotal.dineIn);
      gt.getCell(7).value = Number(result.grandTotal.takeAway);
      gt.getCell(8).value = Number(result.grandTotal.delivery);
      result.paymentMethodColumns.forEach((pm, i) => {
        gt.getCell(9 + i).value = Number(result.grandTotal.byPaymentMethod[pm] || 0);
      });
      gt.eachCell({ includeEmpty: true }, c => {
        c.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 13 };
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1565C0' } };
      });

      // Column widths
      const widths = [40, 14, 12, 12, 14, 12, 12, 12];
      result.paymentMethodColumns.forEach(() => widths.push(12));
      ws.columns = widths.map(w => ({ width: w }));

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `DailySalesSummary_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      toast.success(isRTL ? 'تم التصدير' : 'Exported');
    } catch {
      toast.error(isRTL ? 'فشل التصدير' : 'Export failed');
    }
  };

  // ─── PDF Export ────────────────────────────────────────────────────
  const exportToPdf = () => {
    if (!result) return;
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
      const pw = doc.internal.pageSize.width;
      if (companyInfo?.logoBase64) { try { doc.addImage(companyInfo.logoBase64, 'PNG', 12, 8, 30, 25); } catch {} }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(20); doc.setTextColor(21, 101, 192);
      doc.text(compName || '', pw / 2, 15, { align: 'center' });
      doc.setFillColor(21, 101, 192); doc.roundedRect(pw / 2 - 100, 32, 200, 12, 3, 3, 'F');
      doc.setFontSize(13); doc.setTextColor(255, 255, 255);
      doc.text('Daily Sales Summary', pw / 2, 39.5, { align: 'center' });

      const headers = ['Group', 'Gross', 'Disc', 'Service', 'Net', 'Dine-In', 'Take-Away', 'Delivery', ...result.paymentMethodColumns];
      const body: any[] = [];
      const flatten = (nodes: GroupNode[], depth: number) => {
        nodes.forEach(n => {
          const indent = '  '.repeat(depth);
          body.push([
            `${indent}[${getGroupLabel(n.groupBy)}] ${n.groupValue} (${n.aggregate.recordsCount})`,
            fmt(n.aggregate.gross), fmt(n.aggregate.discount), fmt(n.aggregate.serviceCharge),
            fmt(n.aggregate.net), fmt(n.aggregate.dineIn), fmt(n.aggregate.takeAway), fmt(n.aggregate.delivery),
            ...result.paymentMethodColumns.map(pm => fmt(n.aggregate.byPaymentMethod[pm] || 0)),
          ]);
          if (n.children?.length) flatten(n.children, depth + 1);
        });
      };
      flatten(result.tree, 0);
      body.push([
        'GRAND TOTAL',
        fmt(result.grandTotal.gross), fmt(result.grandTotal.discount), fmt(result.grandTotal.serviceCharge),
        fmt(result.grandTotal.net), fmt(result.grandTotal.dineIn), fmt(result.grandTotal.takeAway), fmt(result.grandTotal.delivery),
        ...result.paymentMethodColumns.map(pm => fmt(result.grandTotal.byPaymentMethod[pm] || 0)),
      ]);

      autoTable(doc, {
        head: [headers],
        body,
        startY: 50,
        theme: 'grid',
        headStyles: { fillColor: [21, 101, 192], textColor: [255, 255, 255], fontSize: 9, fontStyle: 'bold', halign: 'center' },
        bodyStyles: { fontSize: 8 },
        columnStyles: { 0: { cellWidth: 70, halign: 'left' } },
        didParseCell: (data) => {
          if (data.section === 'body' && data.row.index === body.length - 1) {
            data.cell.styles.fillColor = [21, 101, 192];
            data.cell.styles.textColor = [255, 255, 255];
            data.cell.styles.fontStyle = 'bold';
          }
        },
      });

      doc.save(`DailySalesSummary_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(isRTL ? 'تم التصدير' : 'Exported');
    } catch {
      toast.error(isRTL ? 'فشل التصدير' : 'Export failed');
    }
  };

  // ─── Print ─────────────────────────────────────────────────────────
  const handlePrint = () => {
    if (!result) return;
    const w = window.open('', '_blank', 'width=1400,height=900'); if (!w) return;

    const headerCols = [
      isRTL ? 'المجموعة' : 'Group',
      isRTL ? 'إجمالي' : 'Gross',
      isRTL ? 'خصم' : 'Disc',
      isRTL ? 'خدمة' : 'Service',
      isRTL ? 'الصافي' : 'Net',
      isRTL ? 'صالة' : 'Dine-In',
      isRTL ? 'سفري' : 'Take-Away',
      isRTL ? 'دليفري' : 'Delivery',
      ...result.paymentMethodColumns,
    ];

    const rowsHtml: string[] = [];
    const groupColors = ['#D0E2F4', '#D0EFD0', '#FFE0B2', '#E1BEE7', '#FFCDD2'];
    const flatten = (nodes: GroupNode[], depth: number) => {
      nodes.forEach(n => {
        const indent = '&nbsp;&nbsp;&nbsp;&nbsp;'.repeat(depth);
        const bgColor = groupColors[depth % groupColors.length];
        rowsHtml.push(`<tr style="background:${bgColor};${depth === 0 ? 'font-weight:bold' : ''}">
          <td>${indent}<span style="background:#1565C0;color:white;padding:1px 6px;border-radius:6px;font-size:9px">${getGroupLabel(n.groupBy)}</span> ${isRTL ? (n.groupValueAr || n.groupValue) : n.groupValue} <small>(${n.aggregate.recordsCount})</small></td>
          <td style="text-align:right">${fmt(n.aggregate.gross)}</td>
          <td style="text-align:right;color:#E65100">${fmt(n.aggregate.discount)}</td>
          <td style="text-align:right">${fmt(n.aggregate.serviceCharge)}</td>
          <td style="text-align:right;color:#1565C0;font-weight:bold">${fmt(n.aggregate.net)}</td>
          <td style="text-align:right">${fmt(n.aggregate.dineIn)}</td>
          <td style="text-align:right">${fmt(n.aggregate.takeAway)}</td>
          <td style="text-align:right">${fmt(n.aggregate.delivery)}</td>
          ${result.paymentMethodColumns.map(pm => `<td style="text-align:right">${fmt(n.aggregate.byPaymentMethod[pm] || 0)}</td>`).join('')}
        </tr>`);
        if (n.children?.length) flatten(n.children, depth + 1);
      });
    };
    flatten(result.tree, 0);

    const grandTotalRow = `<tr style="background:#1565C0;color:white;font-weight:bold;font-size:13px">
      <td>${isRTL ? 'الإجمالي العام' : 'GRAND TOTAL'}</td>
      <td style="text-align:right">${fmt(result.grandTotal.gross)}</td>
      <td style="text-align:right">${fmt(result.grandTotal.discount)}</td>
      <td style="text-align:right">${fmt(result.grandTotal.serviceCharge)}</td>
      <td style="text-align:right">${fmt(result.grandTotal.net)}</td>
      <td style="text-align:right">${fmt(result.grandTotal.dineIn)}</td>
      <td style="text-align:right">${fmt(result.grandTotal.takeAway)}</td>
      <td style="text-align:right">${fmt(result.grandTotal.delivery)}</td>
      ${result.paymentMethodColumns.map(pm => `<td style="text-align:right">${fmt(result.grandTotal.byPaymentMethod[pm] || 0)}</td>`).join('')}
    </tr>`;

    w.document.write(`<html dir="${isRTL ? 'rtl' : 'ltr'}"><head><title>${reportTitle}</title><style>
      body{font-family:'Segoe UI','Tajawal',sans-serif;margin:15px;color:#333}
      .header{display:flex;align-items:center;justify-content:space-between;border-bottom:3px solid #1565C0;padding-bottom:10px;margin-bottom:10px}
      .header img{max-height:60px} .header h1{color:#1565C0;margin:0;font-size:20px}
      .kpi{display:grid;grid-template-columns:repeat(5,1fr);gap:8px;margin:12px 0}
      .k{padding:10px;border-radius:8px;color:white;text-align:center}
      .k b{font-size:18px;display:block} .k small{opacity:0.9;font-size:10px}
      table{width:100%;border-collapse:collapse;font-size:10px;margin-top:6px}
      th{background:#1565C0;color:white;padding:6px;text-align:${isRTL ? 'right' : 'left'};border:1px solid #1565C0;font-size:10px}
      td{padding:5px 7px;border:1px solid #ddd}
      @media print{body{margin:8px}}
    </style></head><body>
    <div class="header">
      <div>${companyInfo?.logoBase64 ? `<img src="${companyInfo.logoBase64}"/>` : ''}</div>
      <div style="text-align:center;flex:1">
        <h1>${reportTitle}</h1>
        <div style="color:#666;font-size:11px">${compName || ''}</div>
        <div style="color:#666;font-size:10px;margin-top:2px">
          ${result.fromDate ? `${isRTL ? 'من' : 'From'}: ${new Date(result.fromDate).toLocaleDateString('en-GB')}` : ''}
          ${result.toDate ? ` &nbsp; ${isRTL ? 'إلى' : 'To'}: ${new Date(result.toDate).toLocaleDateString('en-GB')}` : ''}
          &nbsp; ${isRTL ? 'مجمّع حسب' : 'Grouped by'}: <b>${result.groupBy.map(getGroupLabel).join(' → ')}</b>
        </div>
      </div>
      <div style="width:80px"></div>
    </div>
    <div class="kpi">
      <div class="k" style="background:linear-gradient(135deg,#1565C0,#42A5F5)"><b>${result.grandTotal.recordsCount}</b><small>${isRTL ? 'سجلات' : 'Records'}</small></div>
      <div class="k" style="background:linear-gradient(135deg,#2E7D32,#66BB6A)"><b>${fmt(result.grandTotal.gross)}</b><small>${isRTL ? 'إجمالي' : 'Gross'}</small></div>
      <div class="k" style="background:linear-gradient(135deg,#FF8C00,#FFA726)"><b>${fmt(result.grandTotal.discount)}</b><small>${isRTL ? 'الخصم' : 'Discount'}</small></div>
      <div class="k" style="background:linear-gradient(135deg,#6A1B9A,#AB47BC)"><b>${fmt(result.grandTotal.serviceCharge)}</b><small>${isRTL ? 'الخدمة' : 'Service'}</small></div>
      <div class="k" style="background:linear-gradient(135deg,#C62828,#EF5350)"><b>${fmt(result.grandTotal.net)}</b><small>${isRTL ? 'الصافي (KWD)' : 'Net (KWD)'}</small></div>
    </div>
    <table>
      <thead><tr>${headerCols.map(h => `<th>${h}</th>`).join('')}</tr></thead>
      <tbody>${rowsHtml.join('')}${grandTotalRow}</tbody>
    </table>
    <div style="text-align:center;margin-top:14px;color:#999;font-size:9px">${new Date().toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}</div>
    </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  // ─── Render ─────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, direction: isRTL ? 'rtl' : 'ltr' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ background: 'linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            {reportTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isRTL
              ? 'ملخص مرن للمبيعات اليومية بعرض شجري قابل للترتيب — اعمل Group بأي ترتيب تريده'
              : 'Flexible tree-based sales summary — group by any combination in any order'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="contained" startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
            onClick={loadReport} disabled={loading}
            sx={{ background: 'linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)', px: 3, borderRadius: 3 }}>
            {loading ? (isRTL ? 'جاري...' : 'Loading...') : (isRTL ? 'عرض' : 'Load')}
          </Button>
          {result && (<>
            <Button variant="contained" size="small" startIcon={<ExcelIcon />} onClick={exportToExcel}
              sx={{ background: 'linear-gradient(45deg, #4CAF50 30%, #66BB6A 90%)', color: 'white', borderRadius: 3 }}>Excel</Button>
            <Button variant="contained" size="small" startIcon={<PdfIcon />} onClick={exportToPdf}
              sx={{ background: 'linear-gradient(45deg, #f44336 30%, #e57373 90%)', color: 'white', borderRadius: 3 }}>PDF</Button>
            <Button variant="contained" size="small" startIcon={<PrintIcon />} onClick={handlePrint}
              sx={{ background: 'linear-gradient(45deg, #FF9800 30%, #FFB74D 90%)', color: 'white', borderRadius: 3 }}>
              {isRTL ? 'طباعة' : 'Print'}
            </Button>
          </>)}
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker label={isRTL ? 'من تاريخ' : 'From'} value={fromDate} onChange={setFromDate}
              format="dd/MM/yyyy" minDate={minDate} maxDate={toDate || maxDate}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }} />
            <DatePicker label={isRTL ? 'إلى تاريخ' : 'To'} value={toDate} onChange={setToDate}
              format="dd/MM/yyyy" minDate={fromDate || minDate} maxDate={maxDate}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }} />
          </LocalizationProvider>
          <Autocomplete multiple size="small" sx={{ minWidth: 240, flex: 1 }}
            options={branches}
            getOptionLabel={(o: any) => isRTL ? (o.branchNameAr || o.branchName) : o.branchName}
            value={branches.filter(b => selectedBranches.includes(b.branchId))}
            onChange={(_, v) => setSelectedBranches(v.map((b: any) => b.branchId))}
            renderInput={p => <TextField {...p} label={isRTL ? 'الفروع' : 'Branches'} placeholder={isRTL ? 'الكل' : 'All'} />}
            renderTags={(value, getTagProps) =>
              value.map((option: any, index: number) => (
                <Chip {...getTagProps({ index })} key={option.branchId} size="small"
                  label={isRTL ? option.branchNameAr || option.branchName : option.branchName} />
              ))
            } />
          <Autocomplete multiple size="small" sx={{ minWidth: 240, flex: 1 }}
            options={concepts}
            getOptionLabel={(o: any) => `${o.conceptCode} - ${isRTL ? (o.conceptNameAr || o.conceptName) : o.conceptName}`}
            value={concepts.filter((c: any) => selectedConcepts.includes(c.conceptId))}
            onChange={(_, v) => setSelectedConcepts(v.map((c: any) => c.conceptId))}
            renderInput={p => <TextField {...p} label={isRTL ? 'الكونسبت' : 'Concepts'} placeholder={isRTL ? 'الكل' : 'All'} />} />
        </Stack>
      </Paper>

      {/* Group-By Builder */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 3, border: '2px dashed', borderColor: 'primary.main' }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountTree fontSize="small" />
          {isRTL ? 'بناء التجميع الشجري (اسحب الأيقونات بالترتيب الذي تريد)' : 'Tree Grouping Builder (drag icons in your desired order)'}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {groupBy.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              {isRTL ? 'اختر طريقة تجميع واحدة على الأقل' : 'Select at least one group-by'}
            </Typography>
          )}
          {groupBy.map((g, idx) => {
            const opt = GROUP_OPTIONS.find(o => o.value === g);
            return (
              <React.Fragment key={g}>
                <Chip
                  icon={<DragIndicator />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>{idx + 1}.</Typography>
                      {opt?.icon}
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>{getGroupLabel(g)}</Typography>
                    </Box>
                  }
                  onDelete={() => removeGroupBy(g)}
                  deleteIcon={<CloseIcon />}
                  sx={{ bgcolor: '#1565C0', color: 'white', '& .MuiChip-deleteIcon': { color: 'white' } }}
                />
                {idx > 0 && (
                  <IconButton size="small" onClick={() => moveGroupBy(idx, -1)} title={isRTL ? 'لأعلى' : 'Move up'}>↑</IconButton>
                )}
                {idx < groupBy.length - 1 && (
                  <IconButton size="small" onClick={() => moveGroupBy(idx, 1)} title={isRTL ? 'لأسفل' : 'Move down'}>↓</IconButton>
                )}
                {idx < groupBy.length - 1 && (
                  <Typography variant="h6" color="primary" sx={{ mx: 0.5 }}>→</Typography>
                )}
              </React.Fragment>
            );
          })}
        </Stack>
        <Divider sx={{ my: 1 }} />
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          {isRTL ? 'إضافة:' : 'Add:'}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {GROUP_OPTIONS.filter(o => !groupBy.includes(o.value)).map(opt => (
            <Button key={opt.value} variant="outlined" size="small" startIcon={<AddIcon />}
              onClick={() => addGroupBy(opt.value)}
              sx={{ borderRadius: 3 }}>
              {opt.icon}
              <Box sx={{ ml: 1 }}>{isRTL ? opt.labelAr : opt.labelEn}</Box>
            </Button>
          ))}
        </Stack>
      </Paper>

      {result && (
        <>
          {/* KPI Dashboard */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
            {[
              { label: isRTL ? 'سجلات' : 'Records', value: result.grandTotal.recordsCount, color: ['#1565C0', '#42A5F5'] },
              { label: isRTL ? 'إجمالي المبيعات' : 'Gross Sales', value: fmt(result.grandTotal.gross), color: ['#2E7D32', '#66BB6A'] },
              { label: isRTL ? 'الخصم' : 'Discount', value: fmt(result.grandTotal.discount), color: ['#FF8C00', '#FFA726'] },
              { label: isRTL ? 'الخدمة' : 'Service', value: fmt(result.grandTotal.serviceCharge), color: ['#6A1B9A', '#AB47BC'] },
              { label: isRTL ? 'الصافي (KWD)' : 'Net (KWD)', value: fmt(result.grandTotal.net), color: ['#C62828', '#EF5350'] },
            ].map((k, i) => (
              <Card key={i} sx={{ background: `linear-gradient(135deg, ${k.color[0]} 0%, ${k.color[1]} 100%)`, color: 'white', borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>{k.label}</Typography>
                  <Typography variant="h5" fontWeight="bold">{k.value}</Typography>
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Tree Table */}
          <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: isDark ? '#1e293b' : '#f8fafc' }}>
              <Typography variant="subtitle2" fontWeight={700}>
                {isRTL ? 'العرض الشجري — مجمّع حسب: ' : 'Tree View — Grouped by: '}
                <span style={{ color: '#1565C0' }}>{groupBy.map(getGroupLabel).join(' → ')}</span>
              </Typography>
              <Box>
                <Button size="small" onClick={expandAll}>{isRTL ? 'فتح الكل' : 'Expand All'}</Button>
                <Button size="small" onClick={collapseAll}>{isRTL ? 'طي الكل' : 'Collapse All'}</Button>
              </Box>
            </Box>
            <TableContainer sx={{ maxHeight: '70vh', overflowX: 'hidden', width: '100%' }}>
              <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%', '& .MuiTableCell-root': { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '10px', p: '4px' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold', fontSize: '10px !important', width: '22%' }}>
                      {isRTL ? 'المجموعة' : 'Group'}
                    </TableCell>
                    <TableCell align="right" sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold', fontSize: '10px !important' }}>{isRTL ? 'إجمالي' : 'Gross'}</TableCell>
                    <TableCell align="right" sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold', fontSize: '10px !important' }}>{isRTL ? 'خصم' : 'Disc'}</TableCell>
                    <TableCell align="right" sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold', fontSize: '10px !important' }}>{isRTL ? 'خدمة' : 'Svc'}</TableCell>
                    <TableCell align="right" sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold', fontSize: '10px !important' }}>{isRTL ? 'الصافي' : 'Net'}</TableCell>
                    <TableCell align="right" sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold', fontSize: '9px !important' }}>{isRTL ? 'صالة' : 'Dine'}</TableCell>
                    <TableCell align="right" sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold', fontSize: '9px !important' }}>{isRTL ? 'سفري' : 'TKA'}</TableCell>
                    <TableCell align="right" sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold', fontSize: '9px !important' }}>{isRTL ? 'دليفري' : 'Dlv'}</TableCell>
                    {result.paymentMethodColumns.map(pm => (
                      <TableCell key={pm} align="right" sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold', fontSize: '9px !important' }} title={pm}>
                        {pm.length > 6 ? pm.substring(0, 6) : pm}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.tree.map((node, i) => renderNode(node, `L0_${i}`, 0))}
                  {/* Grand Total */}
                  <TableRow sx={{ background: 'linear-gradient(90deg, #1565C0 0%, #42A5F5 100%)' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '11px !important' }}>
                      🏆 {isRTL ? 'الإجمالي العام' : 'GRAND TOTAL'} ({result.grandTotal.recordsCount})
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', fontSize: '10px !important' }}>{fmt(result.grandTotal.gross)}</TableCell>
                    <TableCell align="right" sx={{ color: '#FFE0B2', fontWeight: 'bold', fontSize: '10px !important' }}>{fmt(result.grandTotal.discount)}</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', fontSize: '10px !important' }}>{fmt(result.grandTotal.serviceCharge)}</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', fontSize: '11px !important' }}>{fmt(result.grandTotal.net)}</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontSize: '9px !important' }}>{fmt(result.grandTotal.dineIn)}</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontSize: '9px !important' }}>{fmt(result.grandTotal.takeAway)}</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontSize: '9px !important' }}>{fmt(result.grandTotal.delivery)}</TableCell>
                    {result.paymentMethodColumns.map(pm => (
                      <TableCell key={pm} align="right" sx={{ color: 'white', fontSize: '9px !important' }}>
                        {fmt(result.grandTotal.byPaymentMethod[pm] || 0)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {!result && !loading && (
        <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 3 }}>
          <AttachMoney sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {isRTL ? 'حدد الفترة وطريقة التجميع ثم اضغط "عرض"' : 'Set period and grouping then click "Load"'}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default DailySalesSummaryReport;
